import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class ComplementaryProducts extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (e) => {
    const btn = e.target.closest('.complementary-products__add-btn');
    if (!btn || btn.disabled) return;
    // If it's a link (link-to-product mode), let the browser navigate naturally
    if (btn.classList.contains('complementary-products__add-btn--link')) return;
    this.#addToCart(btn);
  };

  async #addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = '…';

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });

      const data = await response.json();

      if (data.status) {
        btn.textContent = originalText;
        btn.disabled = false;
        return;
      }

      btn.textContent = '✓';
      btn.classList.add('complementary-products__add-btn--added');

      document.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'complementary-products',
          itemCount: 1,
        })
      );

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        btn.classList.remove('complementary-products__add-btn--added');
      }, 1500);
    } catch {
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId } = this.dataset;
    const id = this.id;

    if (!productId || !id) return;
    if (recommendationsPerformed === 'true') return;

    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    fetch(url, { signal: this.#activeFetch.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        const el = div.querySelector(`complementary-products[id="${id}"]`);
        const hasProducts = el?.querySelector('.complementary-products__list');
        if (hasProducts) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = el.innerHTML;
        } else {
          this.remove();
        }
      })
      .catch(() => {
        this.remove();
      })
      .finally(() => {
        this.#activeFetch = null;
      });
  }
}

if (!customElements.get('complementary-products')) {
  customElements.define('complementary-products', ComplementaryProducts);
}
